// useAnalysisPersistence: 跨刷新保存 in-flight analysis
//
// 用法:
//   const { save, load, clear } = useAnalysisPersistence()
//   save(analysisId)         // 启动时
//   const id = load()         // mount 时
//   clear()                   // 完成/失败时

const STORAGE_KEY = 'alphalens:current_analysis'
const COMPLETED_KEY = 'alphalens:last_completed_analysis'

export interface PersistedAnalysis {
  analysisId: string
  query: string
  depth: 'quick' | 'standard' | 'deep'
  startedAt: string
  /** point-in-time 基准日 (ALENS-193): 可选, 缺省实时 */
  tradeDate?: string
}

export interface CompletedAnalysisRef {
  analysisId: string
  query: string
  completedAt: string
}

function save(analysis: PersistedAnalysis): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(analysis))
  } catch (e) {
    // localStorage 可能被禁用 (隐私模式, quota)
    console.warn('persistence save failed:', e)
  }
}

function load(): PersistedAnalysis | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as PersistedAnalysis
    if (!data.analysisId || !data.startedAt) return null
    return data
  } catch {
    return null
  }
}

function clear(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function isStale(data: PersistedAnalysis, maxAgeMs = 24 * 60 * 60 * 1000): boolean {
  // 超过 24 小时视为过期
  const age = Date.now() - new Date(data.startedAt).getTime()
  return age > maxAgeMs
}

function saveCompleted(ref: CompletedAnalysisRef): void {
  try {
    localStorage.setItem(COMPLETED_KEY, JSON.stringify(ref))
  } catch (e) {
    console.warn('completed persistence save failed:', e)
  }
}

function loadCompleted(): CompletedAnalysisRef | null {
  try {
    const raw = localStorage.getItem(COMPLETED_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as CompletedAnalysisRef
    if (!data.analysisId || !data.completedAt) return null
    return data
  } catch {
    return null
  }
}

function clearCompleted(): void {
  try {
    localStorage.removeItem(COMPLETED_KEY)
  } catch {
    // ignore
  }
}

export function useAnalysisPersistence() {
  // 各方法不依赖响应式上下文, 均为模块级无状态实现
  return { save, load, clear, isStale, saveCompleted, loadCompleted, clearCompleted }
}

// 浏览器通知 (带 fallback 到 tab 标题闪动)

async function requestPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

function notify(title: string, body: string, options?: NotificationOptions): void {
  if (!('Notification' in window)) {
    flashTitle(title)
    return
  }
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, { body, icon: '/favicon.svg', ...options })
    } catch {
      // 某些浏览器需要 service worker, 兜底
      flashTitle(title)
    }
  } else {
    flashTitle(title)
  }
}

function flashTitle(text: string): void {
  const orig = document.title
  document.title = `🔔 ${text}`
  setTimeout(() => {
    document.title = orig
  }, 5000)
}

export function useNotification() {
  // 同上, 全部为模块级无状态实现
  return { requestPermission, notify }
}
